import { ReactElement } from 'react';
import {
  AutocompleteArrayInput,
  BooleanField,
  DatagridConfigurable,
  List,
  NullableBooleanInput,
  NumberField,
  NumberInput,
  ReferenceArrayInput,
  ReferenceField,
  SelectArrayInput,
  SelectField,
  TextField,
  TextInput,
  UrlField,
  useCanAccess,
} from 'react-admin';
import { createFilterQuery, DEFAULT_LIST_PAGE_PAGINATION_LIMIT } from '../../utils';
import TruncatedTextField from '../../Components/TruncatedTextField';
import {
  FEED_DELIMITER_CHOICES,
  FEED_DISCRIMINATOR_CHOICES,
  FEED_TEXT_QUALIFIER_CHOICES,
} from '../Merchant/utils';

const filters = (canListMerchants: boolean) =>
  [
    <NumberInput alwaysOn key="filter.id" label="ID" source="equals.id" />,
    canListMerchants && (
      <ReferenceArrayInput
        alwaysOn
        key="merchant"
        label="Merchant"
        source="equals.merchant.id"
        reference="merchants"
      >
        <AutocompleteArrayInput
          filterToQuery={createFilterQuery(['merchant_name'])}
          label="Merchant"
          optionText="attributes.merchant_name"
        />
      </ReferenceArrayInput>
    ),
    <TextInput alwaysOn key="notes.contains" source="contains.notes" label="Notes contain..." />,
    <TextInput alwaysOn key="url.contains" source="contains.url" label="URL contains..." />,
    <NumberInput key="min_price_lt" label="Min Price Less Than" source="lessThan.min_price" />,
    <NumberInput
      key="min_price_gt"
      label="Min Price Greater Than"
      source="greaterThan.min_price"
    />,
    <NumberInput key="max_price_lt" label="Max Price Less Than" source="lessThan.max_price" />,
    <NumberInput
      key="max_price_gt"
      label="Max Price Greater Than"
      source="greaterThan.max_price"
    />,
    <NullableBooleanInput key="filter.add_gst" label="Add GST?" source="equals.add_gst" />,
    <SelectArrayInput
      alwaysOn
      key="feed_type"
      source="equals.discriminator"
      choices={FEED_DISCRIMINATOR_CHOICES}
    />,
  ].filter((i) => i !== false);

export default function FeedList(): ReactElement {
  const merchantListAccess = useCanAccess({ action: 'list', resource: 'merchants' });

  const canListMerchants = merchantListAccess.canAccess ?? false;

  const prefetch: string[] = [];
  if (canListMerchants) prefetch.push('merchant');

  return (
    <List
      filters={filters(canListMerchants)}
      perPage={DEFAULT_LIST_PAGE_PAGINATION_LIMIT}
      sort={{ field: 'id', order: 'DESC' }}
      queryOptions={{ meta: { prefetch } }}
    >
      <DatagridConfigurable
        omit={[
          'attributes.not_to_use_request_extras',
          'attributes.delimiter',
          'attributes.text_qualifier',
          'attributes.has_headers',
          'attributes.header_snapshot',
          'attributes.index_root_path',
          'attributes.index_node_name',
          'attributes.root_path',
          'attributes.offer_node_name',
        ]}
      >
        <TextField label="ID" source="id" />
        <ReferenceField
          label="Merchant"
          source="relationships.merchant.data.id"
          reference="merchants"
        >
          <TextField source="attributes.merchant_name" />
        </ReferenceField>
        <TruncatedTextField label="Notes" source="attributes.notes" length={50} />
        <UrlField label="Feed URL" source="attributes.url" />
        <BooleanField label="Reduced Headers?" source="attributes.not_to_use_request_extras" />
        <NumberField label="Min Price (AUD)" source="attributes.min_price" />
        <NumberField label="Max Price (AUD)" source="attributes.max_price" />
        <BooleanField label="Add GST?" source="attributes.add_gst" />
        <SelectField
          label="Feed Type"
          source="attributes.discriminator"
          choices={FEED_DISCRIMINATOR_CHOICES}
        />
        <SelectField
          label="CSV Delimiter"
          source="attributes.delimiter"
          choices={FEED_DELIMITER_CHOICES}
        />
        <SelectField
          label="CSV Text Qualifier"
          source="attributes.text_qualifier"
          choices={FEED_TEXT_QUALIFIER_CHOICES}
        />
        <BooleanField label="CSV Contains Headers?" source="attributes.has_headers" />
        <TextField label="CSV Header Snapshot" source="attributes.header_snapshot" />
        <TextField label="XML Index Root Path" source="attributes.index_root_path" />
        <TextField label="XML Index Item Node Name" source="attributes.index_node_name" />
        <TextField label="XML Root Path" source="attributes.root_path" />
        <TextField label="XML Item Node Name" source="attributes.offer_node_name" />
      </DatagridConfigurable>
    </List>
  );
}
